/**
 * Build a display name from name parts.
 * Prefers displayName from the API when set.
 */
export function formatDisplayName(
  first?: string | null,
  middle?: string | null,
  surname?: string | null,
  displayName?: string | null,
): string {
  const preferred = (displayName ?? "").trim();
  if (preferred) return preferred;

  return [first, middle, surname]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

export interface FormatDoctorNameOptions {
  /** Add "Dr." prefix when not already present. Defaults to true. */
  withPrefix?: boolean;
  /** Prefix to use when withPrefix is true. Defaults to "Dr.". */
  prefix?: string;
}

/**
 * Format a doctor's name, optionally with a Dr prefix.
 * Prefers displayName from the API when set.
 */
export function formatDoctorName(
  first?: string | null,
  middle?: string | null,
  surname?: string | null,
  displayName?: string | null,
  options: FormatDoctorNameOptions = {},
): string {
  const { withPrefix = true, prefix = "Dr." } = options;
  const name = formatDisplayName(first, middle, surname, displayName);
  if (!name) return "";

  if (!withPrefix) return name;

  const lower = name.toLowerCase();
  if (lower.startsWith("dr.") || lower.startsWith("dr ")) {
    return name;
  }

  return `${prefix} ${name}`;
}

const INITIALS_SKIP = new Set([
  "the",
  "a",
  "an",
  "your",
  "and",
  "of",
  "at",
  "for",
  "dr",
  "dr.",
  "you",
]);

export function initialsFromName(value?: string | null) {
  const parts = String(value || "")
    .replace(/^dr\.?\s+/i, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !INITIALS_SKIP.has(part.toLowerCase().replace(/\.$/, "")));
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Hospitals see initials. Grandmaster sees the full name. */
export function socialPersonName(
  first?: string | null,
  middle?: string | null,
  last?: string | null,
  revealFullName = false,
  displayName?: string | null,
) {
  const full = formatDisplayName(first, middle, last, displayName) || "Doctor";
  return revealFullName ? full : initialsFromName(full);
}
